import React, { useEffect, useMemo } from 'react';
import { Form, Input, Row, Drawer, Button, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  companyFormEnum,
  ICompanyFormValues,
  juridicalStatusEnum,
} from 'app/containers/Company/slice/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCompanyDetails,
  selectCompanyFormState,
} from 'app/containers/Company/slice/selectors';
import { useCompanySlice } from 'app/containers/Company/slice';
import { selectSelectedEnv } from 'app/containers/HiikoEnv/slice/selectors';
import { checkIsPositiveNumber } from 'utils/validationHelpers';
import NumberInput from 'app/components/NumberInput/NumberInput';

const CompanyForm = () => {
  const { t } = useTranslation();

  const { isOpen, type, id, loading } = useSelector(selectCompanyFormState);

  const { company: editCompanyData } = useSelector(selectCompanyDetails);

  const selectedEnv = useSelector(selectSelectedEnv);

  const isCreateFormType = type === 'create';

  const { actions } = useCompanySlice();

  const dispatch = useDispatch();

  const [form] = Form.useForm();

  // Set edit company data
  useEffect(() => {
    if (editCompanyData && !isCreateFormType && form && isOpen) {
      form.setFieldsValue({
        ...editCompanyData,
      });
    }
  }, [editCompanyData, isCreateFormType, isOpen, form]);

  // Clear form on close
  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
    }
  }, [isOpen, form]);

  const onFinish = (values: ICompanyFormValues) => {
    if (!selectedEnv) {
      return;
    }

    if (isCreateFormType) {
      dispatch(actions.createCompany({ envId: selectedEnv.id, values }));
    } else if (id) {
      dispatch(actions.editCompany({ envId: selectedEnv.id, id, values }));
    }
  };

  const onCloseForm = () => {
    dispatch(actions.toggleCompanyForm(false));
  };

  const juridicalStatusOptions = useMemo(
    () => [
      {
        text: t(
          'Company.Form.JuridicalStatusTypes.FarmersFarm',
          "Farmer's farm",
        ),
        value: juridicalStatusEnum.farmersFarm,
      },
      {
        text: t(
          'Company.Form.JuridicalStatusTypes.AgriculturalCompany',
          'Agricultural company',
        ),
        value: juridicalStatusEnum.agriculturalCompany,
      },

      {
        text: t('Company.Form.JuridicalStatusTypes.Cooperative', 'Cooperative'),
        value: juridicalStatusEnum.cooperative,
      },
      {
        text: t(
          'Company.Form.JuridicalStatusTypes.limitedLiabilityCompany',
          'A Limited Liability Company',
        ),
        value: juridicalStatusEnum.limitedLiabilityCompany,
      },
    ],
    [t],
  );

  return (
    <Drawer
      title={
        isCreateFormType
          ? t('Company.Form.CreateCompany', 'Create Company')
          : t('Company.Form.EditCompany', 'Edit Company')
      }
      visible={isOpen}
      onClose={onCloseForm}
      destroyOnClose
      placement="right"
      width={420}
      forceRender={true}
      footer={
        <div
          style={{
            textAlign: 'right',
          }}
        >
          <Button onClick={onCloseForm} style={{ marginRight: 8 }}>
            {t('Company.Form.Cancel', 'Cancel')}
          </Button>
          <Button
            loading={loading}
            form="companyForm"
            key="submit"
            htmlType="submit"
            type="primary"
          >
            {isCreateFormType
              ? t('Company.Form.CreateCompany', 'Create Company')
              : t('Company.Form.EditCompany', 'Edit Company')}
          </Button>
        </div>
      }
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{ layout: 'vertical' }}
        requiredMark="optional"
        id="companyForm"
        onFinish={onFinish}
        scrollToFirstError={true}
      >
        <Form.Item
          label={t('Company.Form.juridicalCode', 'Holding number')}
          name={companyFormEnum.juridicalCode}
          rules={[{ required: true }, { validator: checkIsPositiveNumber }]}
        >
          <NumberInput
            placeholder={t('Company.Form.juridicalCode', 'Holding number')}
          />
        </Form.Item>

        <Form.Item
          label={t('Company.Form.LegalStatus', 'Legal status')}
          name={companyFormEnum.juridicalStatus}
          rules={[{ required: true }]}
        >
          <Select
            placeholder={t('Company.Form.LegalStatus', 'Legal status')}
            getPopupContainer={trigger => trigger.parentNode}
          >
            {juridicalStatusOptions.map(option => (
              <Select.Option value={option.value} key={option.value}>
                {option.text}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={t('Company.Form.CompanyName', 'Company name')}
          name={companyFormEnum.name}
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Company.Form.CompanyName', 'Company name')} />
        </Form.Item>

        <Form.Item
          label={t('Company.Form.CompanyCode', 'Company code')}
          name={companyFormEnum.companyCode}
          rules={[{ required: true }, { validator: checkIsPositiveNumber }]}
        >
          <NumberInput
            placeholder={t('Company.Form.CompanyCode', 'Company code')}
          />
        </Form.Item>

        <Form.Item
          label={t('Company.Form.VatCode', 'VAT code')}
          name={companyFormEnum.vatCode}
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Company.Form.VatCode', 'VAT code')} />
        </Form.Item>

        <Row justify="space-between">
          <Form.Item
            label={t('Company.Form.farmerCode', "Farmer's number")}
            name={companyFormEnum.farmerCode}
            rules={[{ required: false }]}
          >
            <Input
              placeholder={t('Company.Form.farmerCode', "Farmer's number")}
            />
          </Form.Item>

          <Form.Item
            label={t('Company.Form.sprayingCode', 'Spray certificate')}
            name={companyFormEnum.sprayingCode}
            rules={[{ required: false }]}
          >
            <Input
              placeholder={t('Company.Form.sprayingCode', 'Spray certificate')}
            />
          </Form.Item>
        </Row>
        <Form.Item
          label={t('Company.Form.Country', 'Country')}
          name={companyFormEnum.country}
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Company.Form.Country', 'Country')} />
        </Form.Item>
        <Form.Item
          label={t('Company.Form.District', 'District')}
          name={companyFormEnum.county}
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Company.Form.District', 'District')} />
        </Form.Item>

        <Form.Item
          label={t('Company.Form.City', 'City')}
          name={companyFormEnum.city}
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Company.Form.City', 'City')} />
        </Form.Item>

        <Form.Item
          label={t('Company.Form.Street', 'Street')}
          name={companyFormEnum.street}
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Company.Form.Street', 'Street')} />
        </Form.Item>

        <Form.Item
          label={t('Company.Form.HouseNumber', 'House number')}
          name={companyFormEnum.houseNumber}
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Company.Form.HouseNumber', 'House number')} />
        </Form.Item>

        <Form.Item
          label={t('Company.Form.PostalCode', 'Postal code')}
          name={companyFormEnum.postalCode}
          rules={[{ required: true }]}
        >
          <Input placeholder={t('Company.Form.PostalCode', 'Postal code')} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default CompanyForm;
